import Chart from 'react-google-charts';
import { ChartDataLoaded } from '@g17eco/types/insight-custom-dashboard';
import { chartStyleNames, getChartOptions, toChartProps } from '@routes/summary/insights/utils/chartStyles';
import { ChartType, chartTooltipOptions } from '@routes/summary/insights/utils/constants';
import { TakenFromText } from './common/TakenFromText';
import { BaseChartComponentProps, BaseHistoricalUtr, ChartDataProps } from '../types';
import { Note } from './common/Note';
import { FormulaVariables } from '@utils/formula';
import { useChartData } from './useChartData';
import { hasChartDataRows } from './multi-utrs-chart/utils';

export interface BarChartDataLoaded<T = unknown> extends ChartDataLoaded<T> {
  variablesWithValues?: FormulaVariables;
}

export type Props<T extends BaseHistoricalUtr> = BaseChartComponentProps<T> & {
  getChartData: (props: ChartDataProps<T>) => Promise<BarChartDataLoaded>;
};

export const BarChart = <T extends BaseHistoricalUtr>(props: Props<T>) => {
  const { getChartData, utrsData, variables, calculation, note, overrideOptions } = props;
  const data = useChartData(getChartData, props);

  if (!utrsData.length || !variables || !calculation || !data || !hasChartDataRows(data.chartData)) {
    return null;
  }

  const { chartData, effectiveDate = '', period, variablesWithValues } = data;

  return (
    <>
      {variablesWithValues ? <Note note={note} variablesWithValues={variablesWithValues} /> : null}
      <div className='w-100 h-100' data-testid='bar-chart'>
        <Chart
          chartType={ChartType.BarChart}
          data={chartData}
          width={'100%'}
          height={'100%'}
          options={{
            ...getChartOptions({
              chartType: ChartType.BarChart,
              options: chartStyleNames.chartStyleHorizontalBar,
              chartProps: toChartProps(props),
            }),
            tooltip: chartTooltipOptions,
            ...overrideOptions,
          }}
        />
      </div>
      <TakenFromText effectiveDate={effectiveDate} period={period} />
    </>
  );
};
